import { Grid, Typography, FormControl, TextField, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, createTheme, Skeleton, Select, InputLabel, MenuItem, FormControlLabel, Switch, useMediaQuery, Theme, ThemeProvider } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useStyles } from "../../common/appStyles";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { PageEnum, RoleEnum } from "../../types/enums";
import { GetAdminDashboardData, GetRegionList } from "../../services/user-service";
import { showSnackbar } from "../../services/snackbar.service";
import { useNavigate } from "react-router-dom";
import { IRegionDetail } from "../../types/regions";
import { RedirectToDash } from "../RedirectToDash";
import { PageHeader } from "../../components/PageHeader";
import { BannerAd } from "../../components/main-layout/BannerAd";

/**
 * @remarks a table of all submissions for a particular region for
 * a particular competency for a particular kind of case (cases for action, cases in progress, etc.)
 * and a Search bar allowing users to search for and view a particular competency.
 * @returns - html page with a table of all submissions for a region
 */
export const AdminDashboard = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.AdminDashboard];
    const user = useSelector((state: RootState) => state.userDetail);
    const [noResult, setNoResult] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [CasesForActionCount, setCasesForActionCount] = useState(0);
    const [CasesInProgressCount, setCasesInProgressCount] = useState(0);
    const [CasesDeniedCount, setCasesDeniedCount] = useState(0);
    const [CasesCompletedCount, setCasesCompletedCount] = useState(0);
    const [TotalCasesCount, setTotalCasesCount] = useState(0);
    const [regions, setRegions] = useState<IRegionDetail[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<String[]>([]);
    const [selectedCompetency, setSelectedCompetency] = useState<number[]>([]);
    const [selectedCaseType, setSelectedCaseType] = useState<string[]>([]);
    const [checked, setChecked] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            body1: {
                color: 'black'
            }
        },
    });

    const getAdminDashData = () => {
        GetAdminDashboardData()
            .then(response => {
                setCasesForActionCount(response.casesForActionCount);
                setCasesInProgressCount(response.casesInProgressCount);
                setCasesDeniedCount(response.casesDeniedCount);
                setCasesCompletedCount(response.casesCompletedCount);
                setTotalCasesCount(response.totalCasesCount);
                setIsLoading(false);
            })
            .catch((error) => {
                showSnackbar('Error geting data', 'error', 5000);
                setIsLoading(false);
            });
    }

    const getAllRegionsList = () => {
        GetRegionList()
            .then(response => {
                setRegions(response);
            })
            .catch((error) => {
                showSnackbar('Error Retrieving data', 'error', 5000);
            });
    }

    const handlePageTitle = () => {
        const roleTitles = {
            1: "Admin Dashboard",
            2: "Admin Dashboard",
            3: "Credentialing Manager Dashboard",
            4: "Judge Dashboard",
            default: "Admin Dashboard"
        };
        return roleTitles[user.roleId] || roleTitles.default;
    };

    const handleNavigate = (assigned, caseTypes?, regions?, competencys?, searchQuery?) => {
        navigate("/casedetail",
            {
                state: {
                    caseType: caseTypes || null,
                    region: regions || null,
                    competency: competencys || null,
                    searchQuery: searchQuery || "",
                    assigned: assigned
                }
            });
    }

    const handleRegion = (value) => {
        setSelectedRegions(value);
    }

    const handleCaseType = (value) => {
        setSelectedCaseType(value);
    }

    const handleCompetency = (value) => {
        setSelectedCompetency(value);
    }

    const handleChecked = (value) => {
        setChecked(value);
    }

    useEffect(() => {
        if (currentPage?.read) {
            getAdminDashData();
            getAllRegionsList();
        }
    }, [currentPage, user]);

    return (
        <Fragment>
            <ThemeProvider theme={theme}>
                <Grid
                    container
                    className={classes.wrapper}
                    spacing={1}
                    justifyContent="center"
                    alignItems={'center'}
                >
                    {user.roleId >= RoleEnum.CredentialingManager && !lgUp && <Grid sx={{ marginTop: '64px' }}>
                        <BannerAd />
                    </Grid>}
                    <Grid item sm={12} className='justify-center' padding="5px">
                        {currentPage?.read &&
                            <Grid
                                container
                                spacing={1}
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="center"
                                alignItems="center"
                                className={lgUp ? classes.subWrapper5 : classes.subWrapper2}
                            >
                                <Grid item xs={3}><PageHeader title={handlePageTitle()} onBackClick={undefined} /></Grid>
                                <><Grid item xs={12}>
                                </Grid><Grid
                                    container
                                    justifyContent={'center'}
                                    alignItems={"center"}
                                    spacing={1}
                                    className={classes.gridPaddingTop}
                                >
                                        <Grid item sm={11} className={classes.tableGrid}>
                                            {isLoading ?
                                                <Grid container direction={"column"} rowSpacing={.5}>
                                                    <Grid item xs={12}>
                                                        <Skeleton variant='rectangular' width={"100%"} height={"2vh"} />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Skeleton variant='rounded' width={'100%'} height={'5.2vh'} />
                                                    </Grid>
                                                </Grid>
                                                :
                                                <Grid container alignItems={"center"} justifyContent={"center"}>
                                                    <Grid item xs={10}>
                                                        <Paper variant={"outlined"} elevation={0}>
                                                            <TableContainer>
                                                                <Table stickyHeader size="small" aria-label="school table">
                                                                    <TableHead>
                                                                        <TableRow className={classes.tableHead}>
                                                                            <TableCell key="CasesForAction" align="center">Cases For Action</TableCell>
                                                                            <TableCell key="CasesInProgress" align="center">Cases In Progress</TableCell>
                                                                            <TableCell key="CasesCompleted" align="center">Cases Completed</TableCell>
                                                                            <TableCell key="CasesDenied" align="center">Cases Denied</TableCell>
                                                                            <TableCell key="TotalCases" align="center">Total Cases</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        <TableRow >
                                                                            <TableCell key="CasesForActionCount" align="center" sx={{ fontSize: 24 }}>{CasesForActionCount}</TableCell>
                                                                            <TableCell key="CasesInProgressCount" align="center" sx={{ fontSize: 24 }}>{CasesInProgressCount}</TableCell>
                                                                            <TableCell key="CasesCompletedCount" align="center" sx={{ fontSize: 24 }}>{CasesCompletedCount}</TableCell>
                                                                            <TableCell key="CasesDeniedCount" align="center" sx={{ fontSize: 24 }}>{CasesDeniedCount}</TableCell>
                                                                            <TableCell key="TotalCasesCount" align="center" sx={{ fontSize: 24 }}>{TotalCasesCount}</TableCell>
                                                                        </TableRow>
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        </Paper>
                                                    </Grid>
                                                </Grid>}
                                        </Grid>
                                        <Grid item xs={12} md={3} className={classes.fixGridMargin}><PageHeader title={"Search for a Submission"} onBackClick={undefined} /></Grid>
                                        <Grid item xs={12}>
                                            <Grid container alignItems={"center"} justifyContent={"center"} columnSpacing={1} direction={{ xs: 'column', md: 'row' }} rowSpacing={2}>
                                                <Grid item xs={3}>
                                                    <FormControl variant='outlined' fullWidth>
                                                        <TextField
                                                            type="text"
                                                            label="Email"
                                                            aria-labelledby="Search Email"
                                                            value={searchQuery}
                                                            onChange={(event) => setSearchQuery(event.target.value)}
                                                        >
                                                        </TextField>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item md={2} xs={12} minWidth={{ xs: '20vh', md: '5vh' }}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Region</InputLabel>
                                                        <Select
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "15vh",
                                                                    },
                                                                }
                                                            }}
                                                            multiple
                                                            required
                                                            labelId="Region-label"
                                                            label="Region"
                                                            size='medium'
                                                            value={selectedRegions}
                                                            onChange={(event) => {
                                                                handleRegion(event.target.value);
                                                            }}
                                                        >
                                                            {regions.map((reg) => (
                                                                <MenuItem key={reg.id} value={JSON.stringify(reg)} aria-labelledby={reg.name}>{reg.name}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={1.7} minWidth={{ xs: '20vh', md: '5vh' }}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Case Type</InputLabel>
                                                        <Select
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "20vh",
                                                                    },
                                                                }
                                                            }}
                                                            required
                                                            multiple
                                                            labelId="CaseType-label"
                                                            label="Case Type"
                                                            size='medium'
                                                            value={selectedCaseType}
                                                            onChange={(event) => {
                                                                handleCaseType(event.target.value)
                                                            }}
                                                        >
                                                            <MenuItem value={"Cases For Action"}>Cases For Action</MenuItem>
                                                            <MenuItem value={"Cases In Progress"}>Cases In Progress</MenuItem>
                                                            <MenuItem value={"Cases Completed"}>Cases Completed</MenuItem>
                                                            <MenuItem value={"Cases Denied"}>Cases Denied</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={2} minWidth={{ xs: '20vh', md: '5vh' }}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Competency</InputLabel>
                                                        <Select
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "20vh",
                                                                    },
                                                                }
                                                            }}
                                                            required
                                                            labelId="Competency-label"
                                                            label="Competency"
                                                            multiple
                                                            size='medium'
                                                            value={selectedCompetency}
                                                            onChange={(event) => {
                                                                handleCompetency(event.target.value);
                                                            }}
                                                        >
                                                            <MenuItem value={1}>Growth Mindset</MenuItem>
                                                            <MenuItem value={2}>Creative Problem Solving</MenuItem>
                                                            <MenuItem value={3}>Emotional Intelligence</MenuItem>
                                                            <MenuItem value={4}>Coaching</MenuItem>
                                                            <MenuItem value={5}>Social Influence</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                {user.roleId <= RoleEnum.CredentialingManager && <Grid item xs={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={checked}
                                                                onChange={(event) => handleChecked(event.target.checked)}
                                                                name="showAssigned"
                                                                color="secondary"
                                                            />
                                                        }
                                                        label="Assigned To Me"
                                                    />
                                                </Grid>}
                                                <Grid item xs={1}>
                                                    <Button variant="contained" size="large" aria-labelledby="search" style={{ height: 55 }} color="secondary" onClick={(event) => handleNavigate(checked, selectedCaseType, selectedRegions, selectedCompetency, searchQuery)} fullWidth>
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid alignSelf={"center"} className={classes.tableNoResult}>
                                            {noResult && <FormControl fullWidth><Typography variant="h5">No Results Found</Typography></FormControl>}
                                        </Grid>
                                    </Grid></>
                            </Grid>
                        }
                        {!currentPage?.read && currentPage?.read !== undefined &&
                            <RedirectToDash />
                        }
                    </Grid>
                </Grid>
            </ThemeProvider>
        </Fragment>);
}